import React from "react";

/**
 * Green "scan" feedback that sweeps down the canvas when the student submits
 * their work to the tutor — a downward-moving line plus a brief green tint, so
 * it reads as the tutor scanning the whiteboard. Adapted from the Alloy lesson
 * prototype (TldrawScene.WhiteboardScan).
 *
 * Fired on submit when the student has drawn, and on paper-photo confirm:
 *
 *   window.dispatchEvent(new CustomEvent(WHITEBOARD_SCAN_EVENT))
 *
 * Render it once inside a `relative overflow-hidden` container over the canvas
 * so the sweep clips to the paper area.
 */
export const WHITEBOARD_SCAN_EVENT = "notepad:whiteboard-scan";

// Full effect duration (sweep + tint). Kept in sync with the CSS animations.
const SCAN_MS = 3600;

export function WhiteboardScan() {
  const [scanning, setScanning] = React.useState(false);

  React.useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let restartFrame: number | undefined;
    const cancelRestart = () => {
      if (restartFrame === undefined) return;
      cancelAnimationFrame(restartFrame);
      restartFrame = undefined;
    };
    const stopForReducedMotion = () => {
      if (!reducedMotion.matches) return;
      cancelRestart();
      setScanning(false);
    };
    const handler = () => {
      // Restart cleanly if a scan is already running (rapid re-submits).
      cancelRestart();
      setScanning(false);
      // A skipped animation cannot fire the event that normally unmounts us.
      if (reducedMotion.matches) return;
      restartFrame = requestAnimationFrame(() => {
        restartFrame = undefined;
        setScanning(!reducedMotion.matches);
      });
    };
    window.addEventListener(WHITEBOARD_SCAN_EVENT, handler);
    reducedMotion.addEventListener("change", stopForReducedMotion);
    return () => {
      cancelRestart();
      window.removeEventListener(WHITEBOARD_SCAN_EVENT, handler);
      reducedMotion.removeEventListener("change", stopForReducedMotion);
    };
  }, []);

  if (!scanning) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 8090,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes wb-scan-sweep {
          0% { transform: translate3d(0, -25%, 0); opacity: 0; }
          8% { opacity: 1; }
          92% { opacity: 1; }
          100% { transform: translate3d(0, 100%, 0); opacity: 0; }
        }
        @keyframes wb-scan-tint {
          0%, 100% { opacity: 0; }
          25%, 80% { opacity: 1; }
        }
      `}</style>

      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(28,156,109,0.12)",
          animation: `wb-scan-tint ${SCAN_MS}ms ease-in-out forwards`,
        }}
      />

      {/* Panel-height track, because a percentage in `transform` resolves
          against the element's own box: `translateY(100%)` on the 3px line
          would move it 3px. */}
      <div
        className="wb-scan-track"
        onAnimationEnd={() => setScanning(false)}
        style={{
          position: "absolute",
          inset: 0,
          willChange: "transform",
          animation: `wb-scan-sweep ${SCAN_MS}ms ease-in-out forwards`,
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            height: 3,
            backgroundColor: "rgba(28,156,109,0.9)",
            boxShadow: "0 0 18px 6px rgba(28,156,109,0.5)",
          }}
        />
      </div>
    </div>
  );
}
